import { ListPerson } from "../../../schema/list";

/** One entry of a bulk mutation's `assign` array. */
export interface BulkAssignRow {
	listPersonId: number;
	prospectId?: number;
	lemlistId?: number;
	campaignId?: string;
}

interface BulkAssignParamInput {
	data: ListPerson[];
	/** Checkbox selection — used unless a single row was confirmed from its row menu. */
	selectedItems: number[];
	/** Single-row action from the row menu; takes precedence over the checkbox selection. */
	selectedItemForConfirmation?: ListPerson | null;
}

/**
 * Builds the `assign` array every bulk mutation on the list table sends: either the one row
 * confirmed from its row menu, or the whole checkbox selection. Stale selected ids (no longer in
 * the loaded page) are dropped. A row's `campaignId` is the campaign it holds, never one guessed
 * from the list's default or the lead's language (PREC-4219): routing decides on the server.
 *
 * The extra fields beyond `listPersonId` describe the row for the callers that care; the backend
 * re-reads each list person by id, so a caller that does not need them can ignore them.
 */
export const getBulkAssignParam = ({
	data,
	selectedItems,
	selectedItemForConfirmation,
}: BulkAssignParamInput): BulkAssignRow[] => {
	const rows: Array<{ listPersonId: number; row?: ListPerson }> = selectedItemForConfirmation
		? [
				{
					listPersonId: selectedItemForConfirmation.id,
					row:
						data.find((item) => item.id === selectedItemForConfirmation.id) ??
						selectedItemForConfirmation,
				},
			]
		: selectedItems
				.map((listPersonId) => ({
					listPersonId,
					row: data.find((item) => item.id === listPersonId),
				}))
				.filter(({ row }) => !!row);

	return rows.map(({ listPersonId, row }) => ({
		listPersonId,
		prospectId: row?.prospect?.id,
		lemlistId: row?.list?.lemlists?.at(0)?.id,
		campaignId: row?.campaignId || undefined,
	}));
};
